import {
  type QueryClient,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import { chatRequest } from "./client";
import type { Session } from "../types";

const api = API_ENDPOINTS.webchat;
const sessionKey = ["webchat", "session"] as const;

export const sessionQueryOptions = () =>
  queryOptions({
    queryKey: sessionKey,
    queryFn: ({ signal }) =>
      chatRequest<Session>(api.session, "GET", undefined, signal),
    staleTime: 0,
    retry: false,
  });

/** Re-probe the chat session after the funnel signs in or out, so the launcher follows without a reload. */
export function refreshChatSession(client: QueryClient) {
  return client.invalidateQueries({ queryKey: sessionKey });
}

export function useChatSession() {
  return useQuery({ ...sessionQueryOptions(), refetchOnWindowFocus: true });
}

export function useChatIdentity() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string | null) =>
      chatRequest<Session>(
        api.session,
        memberId ? "POST" : "DELETE",
        memberId ? { memberId } : undefined,
      ),
    onMutate: async () => {
      await client.cancelQueries({ queryKey: ["webchat"] });
    },
    onSuccess: (session) => {
      client.removeQueries({
        predicate: (query) =>
          query.queryKey[0] === "webchat" && query.queryKey[1] !== "session",
      });
      client.setQueryData(sessionKey, session);
    },
  });
}
